import React, { useState, useEffect } from "react";
import { useAuth } from "../../lib/auth";
import { useContent } from "../../lib/content";
import { Field, ImageField, Msg, Section, IconSelect } from "../../components/admin/FormKit";
import { DEFAULT_CATEGORIES } from "../../lib/seed";
import { Save, Grid3x3, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

export default function AdminCategories() {
  const { r2 } = useAuth();
  const { categories, saveCategories } = useContent();
  const [list, setList] = useState(categories);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setList(categories && categories.length ? categories : DEFAULT_CATEGORIES);
  }, [categories]);

  const update = (i, patch) => {
    setList((arr) => {
      const next = [...arr];
      next[i] = { ...next[i], ...patch };
      return next;
    });
  };
  const add = () =>
    setList((arr) => [
      ...arr,
      { key: `cat-${Date.now().toString(36)}`, name_fa: "", description: "", image: "", icon: "Leaf" },
    ]);
  const remove = (i) => {
    if (!window.confirm("حذف این دسته‌بندی؟")) return;
    setList((arr) => arr.filter((_, k) => k !== i));
  };
  const move = (i, dir) => {
    setList((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const next = [...arr];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      const cleaned = list.map((c) => ({
        ...c,
        key: (c.key || "").trim() || `cat-${Math.random().toString(36).slice(2, 8)}`,
      }));
      await saveCategories(r2, cleaned);
      setMsg("دسته‌بندی‌ها ذخیره شد ✓");
    } catch (e) {
      setMsg(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-10" data-testid="admin-categories-panel">
      <Section
        title="دسته‌بندی محصولات"
        icon={Grid3x3}
        desc="این دسته‌ها در صفحه اصلی و فیلتر محصولات استفاده می‌شوند. «کلید» باید انگلیسی و یکتا باشد (برای URL)."
      >
        <div className="space-y-6">
          {list.map((c, i) => (
            <div key={i} className="rounded-2xl border border-honey-200 p-5 bg-honey-50/40">
              <div className="flex items-center justify-between mb-4">
                <div className="font-display font-bold text-ink">#{i + 1} — {c.name_fa || "(بدون نام)"}</div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-2 rounded-full hover:bg-honey-100 disabled:opacity-40" aria-label="بالا">
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} className="p-2 rounded-full hover:bg-honey-100 disabled:opacity-40" aria-label="پایین">
                    <ArrowDown className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                    data-testid={`admin-cat-remove-${i}`}
                    aria-label="حذف"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="کلید (انگلیسی، برای URL)" value={c.key} onChange={(v) => update(i, { key: v })} testid={`admin-cat-key-${i}`} placeholder="honey" />
                <Field label="نام فارسی" value={c.name_fa} onChange={(v) => update(i, { name_fa: v })} testid={`admin-cat-name-${i}`} />
                <Field full label="توضیح کوتاه" value={c.description} onChange={(v) => update(i, { description: v })} testid={`admin-cat-desc-${i}`} />
                <IconSelect label="آیکون" value={c.icon} onChange={(v) => update(i, { icon: v })} testid={`admin-cat-icon-${i}`} />
                <ImageField full label="تصویر دسته" value={c.image} onChange={(v) => update(i, { image: v })} testid={`admin-cat-image-${i}`} />
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={add}
            className="inline-flex items-center gap-1 bg-white border border-honey-200 hover:border-honey-500 text-ink px-4 py-2 rounded-full text-sm"
            data-testid="admin-cat-add"
          >
            <Plus className="w-4 h-4" /> افزودن دسته
          </button>
        </div>
      </Section>

      <div className="flex items-center justify-between sticky bottom-4 bg-paper/95 backdrop-blur border border-honey-200 rounded-full px-4 py-2 shadow-lg">
        <Msg msg={msg} />
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 bg-honey-500 hover:bg-honey-600 text-white font-display font-bold px-5 py-2.5 rounded-full disabled:opacity-60"
          data-testid="admin-categories-save"
        >
          <Save className="w-4 h-4" /> {busy ? "در حال ذخیره..." : "ذخیره"}
        </button>
      </div>
    </form>
  );
}
